/** Consistency check of one compiled art lesson (no world registry: usable by the client to vet a downloaded chunk). */
import { ART_STYLE_IDS, appearStage, type ArtStage } from './art/types';
import { ART_PALETTES } from './worlds/palettes';
import type { Drawing } from './model';

export const PATH_DATA = /^[MLCQZ0-9.\-\s,]+$/i;

const ROLE_FREE = new Set(['line', 'shade']);

/** Consistency of a compiled art lesson: refs, coverage, construction order and colour roles. */
export function checkArtDrawing(d: Drawing): string[] {
  const out: string[] = [];
  const fig = d.art!;
  if (!Array.isArray(fig.layers) || !Array.isArray(fig.guides) || fig.vb?.length !== 2) return ['malformed figure'];
  if (d.viewBox[0] !== fig.vb[0] || d.viewBox[1] !== fig.vb[1]) out.push('viewBox must equal the figure vb');
  const seen = { g: new Set<number>(), l: new Set<number>(), f: new Set<number>(), e: 0 };
  let last = 0;
  d.steps.forEach((st, i) => {
    const stages = st.stage ?? [];
    if (!stages.length) out.push(`step ${i + 1}: missing stage`);
    if (stages.length && stages[0]! < last) out.push(`step ${i + 1}: construction stages go backwards`);
    if (stages.length) last = stages[stages.length - 1]!;
    for (const s of st.strokes) {
      const r = s.ref ?? '';
      const n = Number(r.slice(1));
      if (r === 'e') seen.e++;
      else if (r[0] === 'g' && fig.guides[n]) seen.g.add(n);
      else if (r[0] === 'l' && fig.layers[n]) {
        seen.l.add(n);
        const a = appearStage(fig.layers[n]!);
        if (stages.length && !stages.includes(a as ArtStage)) out.push(`step ${i + 1}: layer ${n} (stage ${a}) drawn in a stage ${stages.join('+')} step`);
      } else if (r[0] === 'f' && fig.layers[n]?.k === 'region') seen.f.add(n);
      else out.push(`step ${i + 1}: bad stroke ref "${r}"`);
    }
  });
  fig.guides.forEach((_, i) => !seen.g.has(i) && out.push(`guide ${i} never drawn`));
  fig.layers.forEach((l, i) => {
    if (!seen.l.has(i)) out.push(`layer ${i} never drawn`);
    if (l.k === 'region' && !seen.f.has(i)) out.push(`region ${i} never coloured`);
  });
  if (fig.guides.length && seen.e !== 1) out.push('guides must be erased exactly once');
  const shapes = [...fig.guides.map((g) => g.d), ...fig.layers.flatMap((l) => (l.clip ? [l.d, l.clip] : [l.d]))];
  for (const s of shapes) if (!s.trim() || !PATH_DATA.test(s)) out.push(`invalid path data "${s.slice(0, 40)}"`);
  for (const st of ART_STYLE_IDS) {
    const pal = ART_PALETTES[st];
    for (const l of fig.layers) {
      const role = l.k === 'ink' ? (l.color ?? 'line') : l.fill;
      if (!ROLE_FREE.has(role) && !pal[role]) out.push(`unknown colour role "${role}" (style ${st})`);
    }
  }
  return [...new Set(out)];
}
